import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { getIcon } from '../utils/icons-library.js';
import { renderScene } from './bento-scenes.js';

/**
 * BENTO GRID — Grid modular asimétrico (estilo Apple / Linear / Vercel).
 *
 * Reutiliza el array `items: [{ name, desc, icon }]` (mismo modelo que
 * features/process/stats) → cero campos nuevos en el schema de la IA y
 * extracción declarativa estándar (ver core/extractor.js: selector .bento-tile).
 *
 * El tamaño de cada tile NO es un dato editable: se deriva de la posición con
 * un patrón ciclico (PATTERN) para lograr una asimetría intencionada sin que
 * la IA/usuario tengan que decidir spans. Así el bloque siempre "cuadra".
 *
 * Variantes (CSS-driven, render único → DRY):
 *   v1 = Mosaico: tiles con hairline, icono arriba, hover lift (Linear).
 *   v2 = Editorial: número/título display gigante, aire, minimal.
 *   v3 = Glow: tiles glass con borde gradiente y glow en el tile grande (Stripe).
 */

// Patrón de tamaños por índice (grid-auto-flow: dense lo recoloca sin huecos).
// lg = 2x2, wide = 2x1, tall = 1x2, std = 1x1.
const PATTERN = ['lg', 'std', 'std', 'wide', 'tall', 'std', 'std', 'wide', 'std', 'std', 'tall', 'std'];

export const bentoBlock = (rawProps, index = 0) => {
    const p = safe(rawProps);
    // `safe()` (utils/contrast.js) YA dejó `p.items` en un array, así que este `|| []` es
    // cinturón, NO un default de contenido: con 0 items este bloque se pinta VACÍO (titular +
    // contenedor sin nada, y sin ningún "+" con el que añadir desde el lienzo). De que no
    // llegue a publicarse así se encarga la guarda de CPU `_fix_bloques_sin_contenido`
    // (llm_sanitizers.py::_ARRAY_DE_CONTENIDO), que descarta el bloque al generar.
    const items = p.items || [];
    const variant = p.variant || 'v1';

    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? "#ffffff" : "#111827";
    const btnIcon = isDark ? "#111827" : "#ffffff";

    // Toolbar por tile (idéntico patrón a process.js → reusa handlers globales:
    // add/borrar/drag/icon-picker, todos keyed por data-array/data-index).
    const itemToolbar = (i) => `
        ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="items" data-index="${i}">+</button>` : ''}
        <button class="item-add-btn card-add-right" data-array="items" data-index="${i}">+</button>
        <div class="item-tools">
            <button class="tool-btn icon-picker-btn" data-array="items" data-index="${i}" title="Cambiar icono" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.palette || window.icons.image}</button>
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn item-delete-btn" data-array="items" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // SVG decorativo de esquina (path orgánico) — solo en tiles grandes de v1/v3.
    const cornerSvg = `<svg class="bento-corner" viewBox="0 0 120 120" fill="none" aria-hidden="true">
        <path d="M120 0 C70 10 40 40 30 90 C26 108 14 118 0 120 L120 120 Z" fill="currentColor" opacity="0.06"/>
        <path d="M120 20 C82 30 56 54 46 96" stroke="currentColor" stroke-width="1.2" fill="none" opacity="0.18"/>
    </svg>`;

    const header = `
        <div class="bento-head">
            ${p.title ? `<h2 data-field="title" contenteditable="true">${p.title}</h2>` : ''}
            ${p.subtitle ? `<p data-field="subtitle" contenteditable="true">${p.subtitle}</p>` : ''}
        </div>`;

    const tilesHtml = items.map((item, i) => {
        const size = PATTERN[i % PATTERN.length];
        const isBig = size === 'lg';
        const stepNum = String(i + 1).padStart(2, '0');
        // Escena de contenido (counter/ticker/terminal). Si es válida sustituye al
        // icono y "llena" el tile con un mini-visual del sector.
        const sceneHtml = renderScene(item.scene);
        // v2 muestra número display gigante; v1/v3 muestran icono.
        const media = (variant === 'v2')
            ? `<span class="bento-num" aria-hidden="true">${stepNum}</span>`
            : `<span class="bento-ico">${item.icon ? getIcon(item.icon, isBig ? 30 : 24) : getIcon('sparkles', isBig ? 30 : 24)}</span>`;
        const decor = (isBig && variant !== 'v2') ? cornerSvg : '';
        return `
        <div class="bento-tile bento-${size} sortable-item stagger-item${sceneHtml ? ' has-scene' : ''}" data-item-index="${i}" data-array-name="items" data-array="items" data-index="${i}" style="--stagger-i:${i};">
            ${itemToolbar(i)}
            <div class="bento-skin" aria-hidden="true">${decor}</div>
            <div class="bento-tile-inner">
                ${sceneHtml || media}
                <div class="bento-tile-text">
                    <h3 data-field="name" contenteditable="true">${item.name || ''}</h3>
                    ${sceneHtml ? '' : `<p data-field="desc" contenteditable="true">${item.desc || ''}</p>`}
                </div>
            </div>
        </div>`;
    }).join('');

    return `
    <section id="bloque-bento-${index}" class="reveal bento-section variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding: 100px 8%;">
        ${header}
        <div class="bento-grid">
            ${tilesHtml}
        </div>
    </section>`;
};
